import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ParchmentBackground from '../../components/ParchmentBackground';
import {
  ArrowLeft, ArrowRight, Calendar, Clock,
  Share2, MessageCircle, Linkedin, Twitter,
} from 'lucide-react';
import { bookExamples } from '../../data/bookExamples';
import '../../styles/BlogPage.css';

const RELATED_TITLES = [
  'Atomic Habits',
  'Ikigai',
  'Think and Grow Rich',
  'The Compound Effect',
  "Life's Amazing Secrets",
  'The ONE Thing',
];

const ShareRow = () => {
  const url = typeof window !== 'undefined' ? window.location.href : 'https://icraftz.com/blogs/why-i-built-icraftz';
  const text = encodeURIComponent('"Do you know what is going to happen if you read one book a week?" — Why I Built iCraftz, by Ashesh Thanawala.');
  const enc = encodeURIComponent(url);
  return (
    <div className="blog-share-row">
      <span className="blog-share-label"><Share2 size={14} /> Share</span>
      <a className="blog-share-btn blog-share-wa" target="_blank" rel="noopener noreferrer"
         href={`https://wa.me/?text=${text}%20${enc}`} data-testid="share-whatsapp">
        <MessageCircle size={14} /> WhatsApp
      </a>
      <a className="blog-share-btn blog-share-li" target="_blank" rel="noopener noreferrer"
         href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc}`} data-testid="share-linkedin">
        <Linkedin size={14} /> LinkedIn
      </a>
      <a className="blog-share-btn blog-share-tw" target="_blank" rel="noopener noreferrer"
         href={`https://twitter.com/intent/tweet?text=${text}&url=${enc}`} data-testid="share-twitter">
        <Twitter size={14} /> Twitter
      </a>
    </div>
  );
};

const WhyIBuiltIcraftzPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Why I Built iCraftz — iCraftz';
  }, []);

  const relatedBooks = useMemo(() => {
    const found = RELATED_TITLES
      .map((t) => bookExamples.find((b) => b.title === t))
      .filter(Boolean);
    return found;
  }, []);

  const handleExplore = (book) => {
    navigate(`/?book=${book.id}#library`);
  };

  return (
    <div className="legal-page blog-post-page">
      <ParchmentBackground />
      <header className="legal-topbar legal-topbar--split">
        <a
          href="https://icraftz.com"
          className="legal-home-link"
          data-testid="blog-post-topbar-logo"
        >
          iCraftz<sup>™</sup>
        </a>
        <a
          href="https://icraftz.com"
          className="legal-topbar-back"
          data-testid="blog-post-back-home-top"
        >
          <ArrowLeft size={14} /> Back to Home
        </a>
      </header>

      <main className="legal-main">
        <article className="legal-article blog-article">
          <span className="blog-category-pill" data-testid="blog-category">Founder's Voice</span>
          <h1 className="legal-title blog-post-title">Why I Built iCraftz</h1>
          <p className="legal-subtitle blog-post-subtitle">
            One question, ₹199, and 49 books that changed everything
          </p>

          <div className="blog-tagline-block">
            <p>"Stop Scrolling, Start Growing."</p>
            <p>"Less Noise. More Thought."</p>
          </div>

          <div className="blog-byline">
            <span><strong>Ashesh Thanawala</strong></span>
            <span><Calendar size={12} /> April 2026</span>
            <span><Clock size={12} /> 4 min read</span>
          </div>

          <ShareRow />

          <div className="legal-divider" />

          <div className="legal-body blog-body">
            <p>In December 2024, I came across an online course that cost ₹199.</p>
            <p>
              The man running it was <strong>Dr. Manjunath M.S.</strong> — a speed reading and mind
              power coach. His pitch was simple. Devastatingly simple.
            </p>

            <blockquote className="blog-pullquote blog-pullquote--orange">
              <p>"Do you know what is going to happen to you if you read one book per week?"</p>
              <cite>— Dr. Manjunath M.S.</cite>
            </blockquote>

            <p>That one question stopped me. I signed up.</p>
            <p>
              For 6 days, I woke up at 5:30 every morning and joined a live session that ran
              until 7:00 AM. Dr. Manjunath didn't just teach speed reading. He rewired something
              fundamental in the way I thought about books — about knowledge — about growth.
            </p>
            <p>
              He taught me that <em>reading without application is just entertainment</em>.
            </p>
            <p>That sentence changed my life.</p>

            <h2>52 Books. 49 Read. One Website.</h2>
            <p>
              Dr. Manjunath's challenge to every participant was bold — read 52 books in 52 weeks.
              One book every single week for a year.
            </p>
            <p>I didn't hit 52.</p>
            <p>I hit 49.</p>
            <p>
              And those 49 books — every one of them read with intention, every insight applied
              to my actual life — quietly transformed the way I think, the way I work, the way I
              see the world.
            </p>
            <p>
              When I built iCraftz, the choice of <strong>49 books in our library</strong> was not
              random. It was personal. These are the books I read. The books that moved me. The
              books I believe every Indian deserves access to.
            </p>
            <p>But here is the most important thing I want you to know about iCraftz:</p>

            <blockquote className="blog-pullquote blog-pullquote--brown">
              <p>
                "We are not asking you to replace the book. We are asking you to discover it — and
                then buy it exclusively through iCraftz."
              </p>
            </blockquote>

            <h2>The Problem Nobody Was Solving</h2>
            <p>After those 49 books, one question kept returning to me.</p>
            <p><em>What about everyone else?</em></p>
            <p>
              What about the millions of Indians who want to grow but never had a Dr. Manjunath
              moment? No framework. No coach. No bridge between reading and real change.
            </p>
            <p>And then the bigger question — the one that built iCraftz:</p>
            <p>
              <strong>What about the 800 million Indians who don't think in English?</strong>
            </p>
            <p>
              95% of the world's self-improvement content exists in one language. Which means a
              student in Vijayawada, a professional in Coimbatore, an entrepreneur in Sangli —
              locked out. Not for lack of desire. But for lack of access.
            </p>
            <p>That felt wrong. Deeply, urgently wrong.</p>

            <h2>What iCraftz Does</h2>
            <p>iCraftz is not a book summary app.</p>
            <p>
              iCraftz is a <strong>Non-Fiction discovery experience that rekindles the Joy of Reading</strong>. We give you
              enough — a Summary, Key Insights, Action Items and a Core Theme — to know whether
              this book will change your life. And then we make it possible for you to buy it
              directly through iCraftz — at a discounted price, in your language, delivered to you.
            </p>
            <p>No middlemen. No marketplace markups. Just the book, the wisdom, and you.</p>
            <p>
              All of this is available in <strong>10 Indian languages</strong> — Hindi, Telugu,
              Tamil, Kannada, Marathi, Bengali, Gujarati, Malayalam, Punjabi and English —
              translated live by AI, on demand.
            </p>
            <p>
              And very soon — working directly with authors and publishers — full vernacular
              editions of these books — subject to publisher agreements — will be made available on iCraftz. Imagine reading{' '}
              <em>Atomic Habits</em> in Telugu. <em>Think and Grow Rich</em> in Kannada.{' '}
              <em>Ikigai</em> in Marathi.
            </p>
            <p>
              <strong>Not a summary. The full book. In your language. Only on iCraftz — subject to publisher agreements.</strong>
            </p>
            <p>That is where we are going.</p>

            <h2>Less Noise. More Thought.</h2>
            <p>
              The algorithm knows you. It uses that knowledge to keep you scrolling — consuming
              without changing, entertained without growing.
            </p>
            <p>Books are the antidote.</p>
            <p>
              Dr. Manjunath taught me that in December 2024 for ₹199 at 5:30 in the morning.
            </p>
            <p>
              iCraftz is my attempt to pass that lesson on — to every Indian who never got that
              wake-up call.
            </p>

            <p className="legal-pull blog-signoff-pull">
              Stop Scrolling. Start Growing.<br />
              Less Noise. More Thought.
            </p>

            <p className="blog-signoff">
              — Ashesh Thanawala<br />
              <em>Founder, iCraftz</em><br />
              <a href="https://icraftz.com">icraftz.com</a> · April 2026
            </p>
          </div>

          <ShareRow />

          {/* Related books */}
          <div className="legal-divider" />
          <h2 className="blog-related-heading">Featured in this story</h2>
          <p className="blog-related-sub">Six of the 49 books that built iCraftz.</p>
          <div className="blog-related-grid">
            {relatedBooks.map((book) => (
              <div key={book.id} className="blog-related-card" data-testid={`blog-related-${book.id}`}>
                <div className="blog-related-cover">
                  <img src={book.image} alt={book.title} loading="lazy" />
                </div>
                <h3 className="blog-related-title">{book.title}</h3>
                <p className="blog-related-author">by {book.author}</p>
                <div className="blog-related-actions">
                  <button
                    type="button"
                    className="blog-related-btn blog-related-btn--explore"
                    onClick={() => handleExplore(book)}
                    data-testid={`blog-related-explore-${book.id}`}
                  >
                    Explore on iCraftz
                  </button>
                  <button
                    type="button"
                    className="blog-related-btn blog-related-btn--buy"
                    title="Coming soon — exclusively on iCraftz"
                    aria-label="Buy on iCraftz — coming soon"
                    data-testid={`blog-related-buy-${book.id}`}
                    disabled
                  >
                    Buy on iCraftz
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Final CTA */}
          <div className="blog-cta-card">
            <h2 className="blog-cta-heading">Ready to find your next book?</h2>
            <p className="blog-cta-sub">
              Discover. Apply. Buy. Read.<br />
              All in your language. Only on iCraftz.
            </p>
            <Link to="/#library" className="legal-cta-btn" data-testid="blog-cta-explore">
              Explore the Library <ArrowRight size={14} />
            </Link>
          </div>

          <div className="legal-back-home-wrap">
            <a
              href="https://icraftz.com"
              className="legal-back-home-link"
              data-testid="blog-post-back-home-bottom"
            >
              ← Back to Home
            </a>
          </div>
        </article>
      </main>

      <footer className="legal-foot">
        <p>
          <Link to="/about">About</Link>
          <span> · </span>
          <Link to="/mission">Mission</Link>
          <span> · </span>
          <Link to="/blogs">Blog</Link>
          <span> · </span>
          <Link to="/partner">Partner</Link>
          <span> · </span>
          <Link to="/for-authors">For Authors</Link>
          <span> · </span>
          <Link to="/contact">Contact</Link>
          <span> · </span>
          <Link to="/privacy">Privacy</Link>
          <span> · </span>
          <Link to="/terms">Terms</Link>
        </p>
        <p className="legal-foot-copy">© {new Date().getFullYear()} iCraftz™. Less Noise · More Thought.</p>
      </footer>
    </div>
  );
};

export default WhyIBuiltIcraftzPage;
